/**
 * mastering-limiter.js — true-peak brickwall limiter (offline, pure JS)
 * window.MasteringLimiter  (also module.exports for node unit tests)
 *
 * The mastering chain's final stage. Because the master renders OFFLINE, a
 * whole-buffer JS pass is strictly better than a per-quantum AudioWorklet: it
 * can use a real look-ahead sliding-minimum and a hard per-sample ceiling clamp
 * that *mathematically guarantees* output true-peak ≤ ceiling — which a one-pole
 * envelope cannot. Nothing reusable in the codebase provided true-peak limiting
 * (acx = sample-peak hard-clip; restoration-loudness = sample-peak, per-channel).
 *
 * Guarantee: for every sample, gain g ≤ ceiling / oversampledTruePeak(n), so the
 * interpolated (inter-sample) peak of the output stays at/below the ceiling.
 * Stereo-linked (one gain across channels) so the image never shifts.
 *
 * API (channels = array of Float32Array, one per channel):
 *   MasteringLimiter.buildEnvelope(channels, sr, opts) -> Float32Array tpBase
 *       per-sample oversampled true-peak magnitude of the (un-gained) signal.
 *   MasteringLimiter.limit(channels, sr, opts) -> { out, maxGrDb, guarded }
 *       opts: { ceilingDb=-1, preGainDb=0, tpBase?, windowMs=20,
 *               oversample=4, halfWidth=8 }
 *       Applies preGain, then brickwall-limits to ceilingDb. Reuse tpBase across
 *       iterations (preGain scales true-peak linearly) to skip the costly FIR.
 *
 * ⚠️ There is no attack / release / look-ahead here any more, and that is the
 * point: those knobs describe a gain that moves fast, and a gain that moves
 * fast is what distorted the bass (see "the gain computer" below, and the
 * report of 19.09.2026). The one knob is `windowMs` — how long the gain is
 * forbidden from moving — and a caller that passes attackMs/releaseMs is
 * silently describing the old shape. They are accepted and ignored, except
 * that createStreamLimiter still reads releaseMs as a floor for its halo.
 */
(function (root) {
  'use strict';

  function dbToLin(db) { return Math.pow(10, db / 20); }

  // Precompute sinc-interpolation kernels for L fractional phases (windowed).
  // `l1` = max over phases of Σ|h[j]| — the operator-norm bound that powers the
  // skip test in buildEnvelope: |interpolated(n)| ≤ l1 · max|x| over the window.
  function buildKernels(L, halfWidth) {
    var T = 2 * halfWidth + 1;
    var win = new Float32Array(T);
    for (var j = 0; j < T; j++) {
      win[j] = 0.42 - 0.5 * Math.cos((2 * Math.PI * j) / (T - 1)) + 0.08 * Math.cos((4 * Math.PI * j) / (T - 1));
    }
    function sinc(x) { return Math.abs(x) < 1e-9 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x); }
    var kern = [], l1 = 0;
    for (var p = 0; p < L; p++) {
      var d = p / L, k = new Float32Array(T), s = 0;
      for (var jj = 0; jj < T; jj++) {
        var kk = jj - halfWidth;            // tap offset relative to sample n
        k[jj] = sinc(d - kk) * win[jj];
        s += Math.abs(k[jj]);
      }
      if (s > l1) l1 = s;
      kern.push(k);
    }
    return { kern: kern, halfWidth: halfWidth, T: T, l1: l1 };
  }

  /* Centered sliding maximum of the stereo-linked |x| over [n-hw, n+hw], as a
     STREAM. The limiter's gain is shared across channels, so the envelope only
     ever needs the loudest channel at each instant; that per-sample value is
     computed once, on demand, and lives only inside the deque.

     This used to be two full-length arrays — abs-max across channels, then its
     sliding maximum — 166 MB of temporaries on user 3450's 7:51 stereo file,
     both alive at the retained peak. Nothing downstream ever needed them as
     arrays: envelopeRange reads localMax(n) strictly in order, and so do both
     envelope builders. The deque itself is a ring holding at most 2·hw+1
     indices (21 at the default halfWidth of 10). Output is bit-identical to the
     two-array version — asserted in limiter-envelope-stream.mjs against a
     literal reference implementation. Measured 2026-07-31. */
  function lmStart(channels, hw) {
    var N = channels[0].length;
    var cap = 2 * hw + 4; if (cap > N + 1) cap = N + 1; if (cap < 2) cap = 2;
    return { ch: channels, nCh: channels.length, N: N, hw: hw, cap: cap,
             dqI: new Int32Array(cap), dqV: new Float32Array(cap),
             head: 0, tail: 0, pushed: 0, pos: 0 };
  }

  // localMax at `n`. Must be consumed forwards: jumping ahead is fine (the
  // skipped samples simply never had their value asked for), going back is not,
  // because the values behind the window are gone.
  function lmAt(st, n) {
    if (n < st.pos - 1) throw new Error('lmAt: the centered max is a forward stream (' + n + ' < ' + st.pos + ')');
    var cap = st.cap, dqI = st.dqI, dqV = st.dqV, ch = st.ch, nCh = st.nCh, N = st.N;
    var upto = n + st.hw; if (upto > N - 1) upto = N - 1;
    var i = st.pushed, c, v, w;
    for (; i <= upto; i++) {
      v = ch[0][i]; if (v < 0) v = -v;
      for (c = 1; c < nCh; c++) { w = ch[c][i]; if (w < 0) w = -w; if (w > v) v = w; }
      while (st.tail > st.head && dqV[(st.tail - 1) % cap] <= v) st.tail--;
      dqI[st.tail % cap] = i; dqV[st.tail % cap] = v; st.tail++;
    }
    st.pushed = i;
    var lo = n - st.hw;
    while (dqI[st.head % cap] < lo) st.head++;
    st.pos = n + 1;
    return dqV[st.head % cap];
  }

  // Exact interpolated peak at sample n over all L fractional phases.
  // Interior samples skip the per-tap bounds checks entirely (hot path).
  function peakAt(channels, n, kern, L, T, hw, N) {
    var best = 0, nCh = channels.length, c, p, j, acc, a, idx;
    var interior = (n >= hw && n + hw < N);
    for (c = 0; c < nCh; c++) {
      var x = channels[c], lo = n - hw;
      for (p = 0; p < L; p++) {
        var kp = kern[p]; acc = 0;
        if (interior) {
          for (j = 0; j < T; j++) acc += kp[j] * x[lo + j];
        } else {
          for (j = 0; j < T; j++) {
            idx = lo + j;
            if (idx < 0) idx = 0; else if (idx >= N) idx = N - 1;
            acc += kp[j] * x[idx];
          }
        }
        a = acc < 0 ? -acc : acc;
        if (a > best) best = a;
      }
    }
    return best;
  }

  /* Envelope setup shared by the sync + async builders.
     THE OPTIMIZATION (measured 2026-07-26, after a Pro user reported the tool
     "зависает часто" on 4-6 minute tracks):
     the 8x/21-tap polyphase interpolation costs ~336 multiply-adds per stereo
     sample — 30.7 s of a 56 s master on a 6-minute file. But the limiter only
     ever *uses* the interpolated peak where it might exceed the ceiling, and on
     real music that is ~0.3% of samples even at +12 dB of pre-gain.
     So: bound it first. |interp(n)| <= l1 * max|x| over the tap window, where
     l1 is the kernel's L1 norm. If localMax(n) * maxGain * l1 <= ceiling, the
     true peak provably cannot reach the ceiling, so req(n) == 1 no matter what
     the exact value is — and we store that safe UPPER BOUND instead of computing
     it. Output of the limiter is therefore IDENTICAL for any gain <= maxGain,
     and merely conservative (never louder, never over the ceiling) above it. */
  function envelopeSetup(channels, opts) {
    var L = opts.oversample || 8, halfWidth = opts.halfWidth || 10;
    var b = buildKernels(L, halfWidth);
    var localMax = lmStart(channels, b.halfWidth);
    // thr = 0 disables skipping (exact full pass) — the back-compat default when
    // the caller doesn't tell us the ceiling/gain it will use.
    var thr = 0;
    if (opts.ceilLin > 0 && isFinite(opts.maxGain) && opts.maxGain > 0) {
      thr = opts.ceilLin / (opts.maxGain * b.l1);
    }
    return { L: L, hw: b.halfWidth, T: b.T, kern: b.kern, l1: b.l1,
             N: channels[0].length, localMax: localMax, thr: thr };
  }

  function envelopeRange(channels, s, tp, from, to) {
    var kern = s.kern, L = s.L, T = s.T, hw = s.hw, N = s.N, lm = s.localMax, thr = s.thr, l1 = s.l1;
    for (var n = from; n < to; n++) {
      var m = lmAt(lm, n);
      if (m <= thr) tp[n] = m * l1;                       // provably below ceiling
      else tp[n] = peakAt(channels, n, kern, L, T, hw, N); // exact
    }
  }

  // Per-sample oversampled true-peak magnitude, stereo-linked (max across
  // channels and fractional phases). Phase 0 (d=0) reproduces the stored sample.
  function buildEnvelope(channels, sr, opts) {
    opts = opts || {};
    var s = envelopeSetup(channels, opts);
    var tp = new Float32Array(s.N);
    envelopeRange(channels, s, tp, 0, s.N);
    tp._maxGain = s.thr > 0 ? opts.maxGain : Infinity;
    return tp;
  }

  // Async, chunked version of buildEnvelope — yields to the event loop between
  // blocks so a long track (3-5 min) doesn't freeze the main thread / trip the
  // browser's "page unresponsive" dialog. Same math as buildEnvelope. onProgress
  // (optional) receives a 0..1 fraction. Used by the mastering chain; the sync
  // buildEnvelope stays for limit()'s internal fallback on short buffers.
  async function buildEnvelopeAsync(channels, sr, opts, onProgress) {
    opts = opts || {};
    var s = envelopeSetup(channels, opts);
    var N = s.N, tp = new Float32Array(N);
    // Time-sliced, not fixed-size: with the skip test the per-sample cost swings
    // ~100x between quiet and loud passages, so a fixed sample count would either
    // stall the frame on a dense chorus or yield pointlessly often on silence.
    var SLICE_MS = 24, STEP = 8192, start = 0;
    while (start < N) {
      var t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      do {
        var end = start + STEP; if (end > N) end = N;
        envelopeRange(channels, s, tp, start, end);
        start = end;
      } while (start < N && ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0) < SLICE_MS);
      if (onProgress) { try { onProgress(start / N); } catch (e) {} }
      await new Promise(function (r) { setTimeout(r, 0); });
    }
    tp._maxGain = s.thr > 0 ? opts.maxGain : Infinity;
    return tp;
  }

  // Sliding minimum over a forward window [n, n+W] via a monotonic deque. O(N).
  // `sc` (optional) supplies reusable scratch so the loudness loop doesn't
  // allocate 2·N arrays on every iteration.
  function slidingMinForward(a, W, sc) {
    var N = a.length;
    // Same size mistake as slidingMaxCentered had: the deque holds indices in
    // [n, n+W] — at most W+1, i.e. ~67 at the default 1.5 ms lookahead — yet it
    // was an Int32Array the length of the track (another 83 MB on a 7:51 file,
    // held for the whole render because the loudness loop reuses this scratch).
    var cap = W + 4; if (cap > N + 1) cap = N + 1; if (cap < 2) cap = 2;
    var out, dq;
    if (sc) {
      if (!sc.lmin || sc.lmin.length !== N) sc.lmin = new Float32Array(N);
      if (!sc.dq || sc.dq.length !== cap) sc.dq = new Int32Array(cap);
      out = sc.lmin; dq = sc.dq;
    } else { out = new Float32Array(N); dq = new Int32Array(cap); }
    var head = 0, tail = 0; // monotonic counters; storage is a ring of `cap`
    // Process from right to left so window is [n, n+W]
    for (var n = N - 1; n >= 0; n--) {
      // push a[n]
      while (tail > head && a[dq[(tail - 1) % cap]] >= a[n]) tail--;
      dq[tail++ % cap] = n;
      // evict indices beyond n+W
      while (dq[head % cap] > n + W) head++;
      out[n] = a[dq[head % cap]];
    }
    return out;
  }

  /* ── the gain computer ──────────────────────────────────────────────────
     A limiter's gain is a signal, and whatever that signal contains is
     multiplied onto the music: a gain that moves at 100 Hz puts a 100 Hz
     modulation on every partial, which on a bass note IS harmonic distortion
     and on everything else is a sideband skirt.

     What used to be here — 1.5 ms look-ahead, a 1 ms attack / 80 ms release
     one-pole, and a per-sample clamp down to the required gain — let the gain
     track the bass WAVEFORM, because the clamp is a step and it fires twice
     per cycle. Measured on a sustained tone at ~9 dB of limiting (the floor of
     the instrument is the same tone unprocessed, shown in brackets):
     20 Hz 7.1 % [1.2], 30 Hz 4.4 % [0.8], 40 Hz 3.2 % [0.6], 60 Hz 1.5 % [0.4]
     — the lower the note the worse, which is the fingerprint of a gain moving
     inside the cycle. Report 19.09.2026 from a club/hardcore producer on
     /music-mastering: «при мастеринге идёт перезагрузка по частотам басс
     бочки». His targets (club −9, cd −9) are where this lives: 30 days of RU
     traffic, club masters average 7.4 dB of gain reduction and 36 % of them
     are over 9 dB, against 1.6 dB on the streaming target.

     The shape now: a minimum held over ±H, then a triangle of the same
     half-width. Not taste — it is the one construction that gives both
     properties at once:
       • the ceiling stays guaranteed WITHOUT a clamp, because every value the
         triangle averages is a minimum taken over a window that contains n,
         so the average is ≤ req[n] by construction;
       • the gain is band-limited to roughly 1/(2H), so it cannot move inside
         a cycle of anything it multiplies.
     H = 20 ms puts the gain's first null at 50 Hz — under the lowest note this
     material carries. Measured with a sustained 41 Hz note under a transient
     train at 10 dB of limiting, against a constant-gain control whose floor is
     0.09 %: old 1.91 %, H=8 ms 0.56 %, H=12.5 ms 0.41 %, H=16 ms 0.25 %,
     H=20 ms 0.05 % (at the floor). Longer buys nothing and costs loudness.

     ⚠️ A slower RELEASE is the obvious-looking fix and it is the wrong one:
     measured at matched loudness, adding an 80–200 ms release on top of this
     window left the bass ripple where it was and cost 7 dB of pre-gain
     (edm-club, −9 LUFS: pre 6.0 → 13.4 dB, GR 7.4 → 14.8). The window is what
     fixes the distortion; a release only takes loudness away.

     Cost of the window itself, at MATCHED output loudness (−9 LUFS, three
     tracks): 0.5–1.5 dB more pre-gain, 0.2–0.5 dB less transient peak, and the
     ripple the gain puts into the audio band falls 2.6×. The loudness given up
     is loudness the old ripple was manufacturing out of distortion. */
  var GAIN_WINDOW_MS = 20;
  function gainWindowSamples(opts, sr) {
    var ms = (opts && opts.windowMs != null) ? opts.windowMs : GAIN_WINDOW_MS;
    return Math.max(1, Math.round(ms / 1000 * sr));
  }

  /* limit() распилен на setup + range-проходы по образцу envelopeSetup /
     envelopeRange выше: sync limit() гоняет каждый проход целиком, а
     limitAsync() — теми же функциями, но ломтями под бюджет времени с
     НАСТОЯЩЕЙ макрозадачей между ломтями. Причина: полнотрековый sync-вызов
     на 2-минутном файле при CPU 4× держит главный поток до 2.9 с (замер
     08.08, класс «еле текст ввёл» у Pro-3450). Математика и порядок
     операций на сэмпл идентичны — страж сверяет побайтово. */
  function limitSetup(channels, sr, opts, tpBase) {
    var ceilingDb = (typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0;
    var preGain = dbToLin(opts.preGainDb || 0);
    // Safety margin below the ceiling: our 8x oversampled detector still
    // under-reads the continuous true peak by a few tenths of a dB vs ffmpeg's
    // ebur128, so aim the ISP clamp a touch lower. Sample-domain clamp stays at
    // the real ceiling. Calibrated against the ffmpeg test battery.
    var margin = (typeof opts.marginDb === 'number') ? opts.marginDb : 0.3;
    var nCh = channels.length, N = channels[0].length;

    // Scratch reuse: the loudness loop calls limit() up to 8 times on the SAME
    // buffer, and each call otherwise allocated req+Lmin+dq+out ≈ 5·N floats —
    // 365 MB per call on a 6-minute stereo track, i.e. GC thrash on the 2 GB
    // machines this tool actually runs on. The caller passes one scratch object
    // and every iteration reuses it.
    // `dest` — write the limited signal straight into the caller's arrays. The
    // chain used to take our scratch output and copy it into its own AudioBuffer
    // on EVERY iteration: one extra full-length buffer alive for the whole
    // render (159 MB on a 7:51 stereo track, of a 987 MB retained peak measured
    // 2026-07-30) plus a full pass of memcpy per pass. Must not alias
    // `channels` — the chain reads bufA and writes the output buffer.
    var dest = opts.dest;
    if (dest && (dest.length !== nCh || !dest[0] || dest[0].length !== N)) dest = null;
    var sc = opts.scratch;
    if (!sc || sc.N !== N || sc.nCh !== nCh) {
      sc = { N: N, nCh: nCh, req: new Float32Array(N), out: null };
      if (opts.scratch) { opts.scratch.N = sc.N; opts.scratch.nCh = sc.nCh; opts.scratch.req = sc.req; opts.scratch.out = null; }
    }
    if (!dest && !sc.out) {
      sc.out = [];
      for (var ci = 0; ci < nCh; ci++) sc.out.push(new Float32Array(N));
      if (opts.scratch) opts.scratch.out = sc.out;
    }
    // Gain-window scratch: one monotonic deque for the minimum and one small
    // ring per box pass, so both boxes can run IN PLACE over lmin instead of
    // materialising another full-length array (90 MB on a 7:51 stereo track).
    var H = gainWindowSamples(opts, sr);
    if (H > N) H = Math.max(1, N);
    // Ring sizes are rounded UP to a power of two: `% cap` on a monotonic
    // counter is a float division in JS, and these are the hot loops.
    var cap = 4; while (cap < 2 * H + 4) cap *= 2;
    var h1 = H >> 1, h2 = H - h1;
    if (!sc.lmin || sc.lmin.length !== N) sc.lmin = new Float32Array(N);
    if (!sc.dqI || sc.dqI.length !== cap) { sc.dqI = new Int32Array(cap); sc.dqV = new Float32Array(cap); }
    var rc1 = 2; while (rc1 < h1 + 2) rc1 *= 2;
    var rc2 = 2; while (rc2 < h2 + 2) rc2 *= 2;
    if (!sc.r1 || sc.r1.length !== rc1) sc.r1 = new Float32Array(rc1);
    if (!sc.r2 || sc.r2.length !== rc2) sc.r2 = new Float32Array(rc2);
    if (opts.scratch) {
      opts.scratch.lmin = sc.lmin; opts.scratch.dqI = sc.dqI; opts.scratch.dqV = sc.dqV;
      opts.scratch.r1 = sc.r1; opts.scratch.r2 = sc.r2;
    }

    return {
      channels: channels, tpBase: tpBase, N: N, nCh: nCh,
      preGain: preGain,
      ceilClamp: dbToLin(ceilingDb - margin),  // ISP target for the gain guarantee
      ceil: dbToLin(ceilingDb),                // sample-domain hard clamp
      H: H, cap: cap,
      req: sc.req, lmin: sc.lmin, dqI: sc.dqI, dqV: sc.dqV, out: dest || sc.out,
      // pass state
      head: 0, tail: 0, pushed: 0, minG: 1, guarded: 0,
      b1: { h: h1, ring: sc.r1, pushed: 0, sum: 0, cnt: 0 },
      b2: { h: h2, ring: sc.r2, pushed: 0, sum: 0, cnt: 0 }
    };
  }

  // Required gain per sample from the PRE-GAINED true-peak (aim at ceilClamp).
  function limitReqRange(st, from, to) {
    var req = st.req, tp = st.tpBase, preGain = st.preGain, ceilClamp = st.ceilClamp;
    for (var n = from; n < to; n++) {
      var t = preGain * tp[n];
      req[n] = t > ceilClamp ? ceilClamp / t : 1;
    }
  }

  /* Minimum of the required gain over [n-H, n+H], written into lmin. Forward
     stream over a monotonic deque, so the state (head/tail/pushed) carries
     across chunk boundaries exactly like lmAt above. */
  function limitMinRange(st, from, to) {
    var a = st.req, out = st.lmin, dqI = st.dqI, dqV = st.dqV, mask = st.cap - 1, H = st.H, N = st.N;
    var head = st.head, tail = st.tail, pushed = st.pushed, v;
    for (var n = from; n < to; n++) {
      var upto = n + H; if (upto > N - 1) upto = N - 1;
      for (; pushed <= upto; pushed++) {
        v = a[pushed];
        while (tail > head && dqV[(tail - 1) & mask] >= v) tail--;
        dqI[tail & mask] = pushed; dqV[tail & mask] = v; tail++;
      }
      var lo = n - H;
      while (dqI[head & mask] < lo) head++;
      out[n] = dqV[head & mask];
    }
    st.head = head; st.tail = tail; st.pushed = pushed;
  }

  /* One centred box pass over lmin, IN PLACE. The window still needs the
     original values behind n after they have been overwritten, so the last
     h+1 of them live in a ring — O(H) instead of another full-length array.
     Two of these in series make the triangle; at the edges the window simply
     shrinks, which keeps the guarantee (n is always inside its own window). */
  function limitBoxRange(st, bx, from, to) {
    var a = st.lmin, N = st.N, h = bx.h, ring = bx.ring, mask = ring.length - 1;
    var pushed = bx.pushed, sum = bx.sum, cnt = bx.cnt;
    for (var n = from; n < to; n++) {
      var upto = n + h; if (upto > N - 1) upto = N - 1;
      for (; pushed <= upto; pushed++) { sum += a[pushed]; cnt++; }
      var rem = n - h - 1;
      if (rem >= 0) { sum -= ring[rem & mask]; cnt--; }
      ring[n & mask] = a[n];               // the ORIGINAL, before we overwrite it
      a[n] = sum / cnt;
    }
    bx.pushed = pushed; bx.sum = sum; bx.cnt = cnt;
  }

  /* Apply. The smoothed gain is ≤ req[n] by construction, so the clamp below
     is arithmetic hygiene (float32 rounding in the running sums), not the
     ceiling mechanism it used to be — st.guarded counts how often it bites and
     the guard test asserts that is a rounding-sized number, never a shape. */
  function limitGainRange(st, from, to) {
    var req = st.req, Lmin = st.lmin, out = st.out, channels = st.channels;
    var preGain = st.preGain, ceil = st.ceil;
    var nCh = st.nCh, minG = st.minG, guarded = st.guarded, c;
    for (var n = from; n < to; n++) {
      var g = Lmin[n];
      if (g > req[n]) { g = req[n]; guarded++; }
      if (g < 0) g = 0;
      if (g < minG) minG = g;                // gr is read off this ONCE, below
      var ga = preGain * g;
      for (c = 0; c < nCh; c++) {
        var y = channels[c][n] * ga;
        if (y > ceil) y = ceil; else if (y < -ceil) y = -ceil; // sample-domain safety
        out[c][n] = y;
      }
    }
    st.minG = minG; st.guarded = guarded;
  }

  /* −20·log10 is monotone, so the deepest reduction is the smallest gain and
     the logarithm belongs OUTSIDE the loop. It used to run once per sample:
     6.5 s of a 9.5 s limiter pass on a 6-minute stereo track, measured. */
  function limitMaxGrDb(st) {
    var g = st.minG < 1e-9 ? 1e-9 : st.minG;
    return -20 * Math.log10(g);
  }

  function limit(channels, sr, opts) {
    opts = opts || {};
    var ceilingDb = (typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0;
    var margin = (typeof opts.marginDb === 'number') ? opts.marginDb : 0.3;
    var tpBase = opts.tpBase || buildEnvelope(channels, sr,
      { oversample: opts.oversample, halfWidth: opts.halfWidth,
        ceilLin: dbToLin(ceilingDb - margin), maxGain: dbToLin(opts.preGainDb || 0) });
    var st = limitSetup(channels, sr, opts, tpBase);
    limitReqRange(st, 0, st.N);
    limitMinRange(st, 0, st.N);
    limitBoxRange(st, st.b1, 0, st.N);
    limitBoxRange(st, st.b2, 0, st.N);
    limitGainRange(st, 0, st.N);
    return { out: st.out, maxGrDb: limitMaxGrDb(st), guarded: st.guarded };
  }

  // Async limit(): те же range-проходы ломтями под бюджет 24 мс с
  // макрозадачей между ломтями (scheduler.yield, где есть). Числа
  // идентичны sync-пути: состояние проходов (deque, g, maxGr) переносится
  // через границы ломтей без изменения порядка операций.
  function _yieldMacro() {
    if (typeof scheduler !== 'undefined' && scheduler.yield) return scheduler.yield();
    return new Promise(function (r) { setTimeout(r, 0); });
  }
  async function limitAsync(channels, sr, opts, onProgress) {
    opts = opts || {};
    var ceilingDb = (typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0;
    var margin = (typeof opts.marginDb === 'number') ? opts.marginDb : 0.3;
    var tpBase = opts.tpBase || await buildEnvelopeAsync(channels, sr,
      { oversample: opts.oversample, halfWidth: opts.halfWidth,
        ceilLin: dbToLin(ceilingDb - margin), maxGain: dbToLin(opts.preGainDb || 0) });
    var st = limitSetup(channels, sr, opts, tpBase);
    var N = st.N, SLICE_MS = 24, STEP = 65536;
    var now = function () { return (typeof performance !== 'undefined' ? performance.now() : Date.now()); };
    // прогресс по цене прохода: req и два бокса дёшевы, минимум и применение
    // дороже. Каждый проход идёт ломтями, состояние переносится через границу.
    var PASSES = [
      [0.15, function (a, b) { limitReqRange(st, a, b); }],
      [0.25, function (a, b) { limitMinRange(st, a, b); }],
      [0.10, function (a, b) { limitBoxRange(st, st.b1, a, b); }],
      [0.10, function (a, b) { limitBoxRange(st, st.b2, a, b); }],
      [0.40, function (a, b) { limitGainRange(st, a, b); }]
    ];
    var base = 0, t0;
    for (var p = 0; p < PASSES.length; p++) {
      var share = PASSES[p][0], step = PASSES[p][1], pos = 0;
      while (pos < N) {
        t0 = now();
        do { var e = pos + STEP; if (e > N) e = N; step(pos, e); pos = e; }
        while (pos < N && now() - t0 < SLICE_MS);
        if (onProgress) { try { onProgress(base + share * pos / N); } catch (err) {} }
        await _yieldMacro();
      }
      base += share;
    }
    return { out: st.out, maxGrDb: limitMaxGrDb(st), guarded: st.guarded };
  }

  /* Streaming wrapper: the SAME limit() run block by block, each block fed
     with one release time of context on both sides, so a file of any length
     limits in O(block) memory.

     Why an overlap and not carried state: limit()'s gain at n is a function of
     the required gain over a window around n, so a block boundary cuts a
     window, not a state. Measured 2026-07-31 per halo size against the
     whole-file render: a "lookahead + kernel" halo (82 samples) leaves −63 dB
     seams on transient material and −49 dB on dynamic; 80 ms (3840 samples
     @48 kHz) is bit-identical on transient material and 1 ULP float32
     (−152.5 dB) on dynamic.

     ⚠️ The halo used to be *defined* as one release time, and that stopped
     being a reason when the gain stopped being a release (see the gain
     computer above): the influence radius is now the minimum window plus the
     triangle, i.e. 2·H = 40 ms at the default. 80 ms still covers it, so the
     measured numbers above still hold — but the halo is now derived from what
     actually reaches across the boundary, so a caller passing a short
     windowMs/releaseMs cannot silently starve it.

     Usage (opts = same knobs as limit(), plus blockSec, default 4 s):
       var lim = MasteringLimiter.createStreamLimiter(sr, nCh, {
           ceilingDb: -1, preGainDb: g, releaseMs: 80, blockSec: 4 });
       var blocks = lim.push(channelArrays);   // 0..n emitted blocks
       var tail = lim.flush();                 // { blocks, maxGrDb }
     Emitted blocks are freshly-allocated arrays (safe to hand to a writer);
     concatenated they are the exact limit() output for the whole stream.

     `opts.emit(views, frames)` (2026-10-01) — the same blocks WITHOUT the
     allocation: views into the limiter's own scratch, valid only during the
     call (copy them out before returning); push() and flush() then return no
     blocks. A caller that writes the output straight back into its own arrays
     (the /mashup-maker master, in place) otherwise made a fresh copy of every
     sample only to throw it away: 114 MB of garbage on a 5-min stereo master at
     48 kHz, at the export's peak on a 4-GB phone (bug report 2026-09-28). */
  function createStreamLimiter(sr, nCh, opts) {
    opts = opts || {};
    var emit = typeof opts.emit === 'function' ? opts.emit : null;
    var relMs = (opts.releaseMs != null) ? opts.releaseMs : 80;
    var HALO = Math.max(1, Math.round(relMs / 1000 * sr), 2 * gainWindowSamples(opts, sr) + 16);
    // Block floor: the one-release halo was measured exact with 2-second
    // blocks; an 80 ms block puts boundaries inside still-recovering release
    // tails and measures −52 dB (limiter-stream-block.mjs pins both facts).
    var B = Math.max(Math.round((opts.blockSec || 4) * sr), 2 * sr, 2 * HALO);
    var limOpts = {
      ceilingDb: opts.ceilingDb, preGainDb: opts.preGainDb, marginDb: opts.marginDb,
      windowMs: opts.windowMs,
      oversample: opts.oversample, halfWidth: opts.halfWidth
    };
    var cap = 2 * HALO + B + 8192;
    var buf = [];
    for (var ci = 0; ci < nCh; ci++) buf.push(new Float32Array(cap));
    var bufStart = 0;      // absolute position of buf[c][0]
    var bufLen = 0;
    var blockStart = 0;    // absolute position of the next un-emitted sample
    var maxGrDb = 0;
    var scratch = {};

    function ensureCap(need) {
      if (need <= buf[0].length) return;
      var ncap = buf[0].length;
      while (ncap < need) ncap *= 2;
      for (var c = 0; c < nCh; c++) {
        var g = new Float32Array(ncap);
        g.set(buf[c].subarray(0, bufLen));
        buf[c] = g;
      }
    }

    // Limit [blockStart, endAbs) with a HALO of real samples on each side
    // (left: re-processed release run-in, discarded; right: real lookahead
    // context, clamped to what the stream actually has — at end-of-stream
    // that clamp reproduces the whole-file render's own edge exactly).
    // Returns the emitted block, freshly allocated.
    function runBlock(endAbs) {
      var fromAbs = blockStart - HALO; if (fromAbs < bufStart) fromAbs = bufStart;
      var toAbs = endAbs + HALO;
      var lastAbs = bufStart + bufLen;
      if (toAbs > lastAbs) toAbs = lastAbs;
      var fo = fromAbs - bufStart, to = toAbs - bufStart;
      var slice = [], c;
      for (c = 0; c < nCh; c++) slice.push(buf[c].subarray(fo, to));
      var r = limit(slice, sr, Object.assign({ scratch: scratch }, limOpts));
      if (r.maxGrDb > maxGrDb) maxGrDb = r.maxGrDb;
      var off = blockStart - fromAbs, len = endAbs - blockStart;
      var out = [];
      for (c = 0; c < nCh; c++) {
        if (emit) { out.push(r.out[c].subarray(off, off + len)); continue; }
        var o = new Float32Array(len);
        o.set(r.out[c].subarray(off, off + len));
        out.push(o);
      }
      if (emit) { emit(out, len); out = null; }
      blockStart = endAbs;
      var drop = (blockStart - HALO) - bufStart;
      if (drop > 0) {
        for (c = 0; c < nCh; c++) buf[c].copyWithin(0, drop, bufLen);
        bufStart += drop; bufLen -= drop;
      }
      return out;
    }

    return {
      // channelArrays: one Float32Array per channel. Returns an array of
      // emitted blocks (possibly empty) — each a channelArrays of its own.
      push: function (channelArrays, frames) {
        var n = frames || (channelArrays[0] ? channelArrays[0].length : 0);
        if (n) {
          ensureCap(bufLen + n);
          for (var c = 0; c < nCh; c++) buf[c].set(channelArrays[c].subarray(0, n), bufLen);
          bufLen += n;
        }
        var emitted = [];
        while (bufStart + bufLen >= blockStart + B + HALO) {
          var eb = runBlock(blockStart + B); if (eb) emitted.push(eb);
        }
        return emitted;
      },
      flush: function () {
        var emitted = [];
        var endAbs = bufStart + bufLen;
        while (blockStart < endAbs) {
          var tb = runBlock(Math.min(blockStart + B, endAbs)); if (tb) emitted.push(tb);
        }
        return { blocks: emitted, maxGrDb: maxGrDb };
      }
    };
  }

  /* ── createFastLimiter — the lookahead SAMPLE-peak limiter (2026-09-12) ──
     Same class as FFmpeg's `alimiter`: a lookahead window (`attackMs`) in
     which the gain ramps down BEFORE the peak, an exponential release behind
     it, one gain for all channels. It exists because limit() above — the
     mastering-grade true-peak stage — costs ~40× a plain gain pass even when
     it reduces nothing: measured in node on 175 s of stereo 48 kHz, envelope
     2.1–5.3 s (oversample 1…4) + gain pass 1.2 s against 0.12 s for gain
     alone; at the 4× CPU throttle of a mid phone that is 8 s, and on the phone
     of the /video-volume reporter (report #2371, ~12× a desktop) 40–80 s for
     a 3-minute clip — the very wait the native lane was built to remove.
     This one is ~0.4 s in node on the same material.

     Guarantee (sample domain): for every n, |out_c[n]| ≤ ceiling. Proof shape:
     gt[n] = min(1, L / max_c|x_c[n]|); m[n] = min gt over [n, n+A); the
     A-sample moving average of m over [n−A+1, n] only averages windows that
     all contain n, so ma[n] ≤ gt[n]; the release step never lifts env above
     ma[n]. The inter-sample peak is NOT bounded — that is limit()'s job. Use
     this where the source lane was alimiter (streaming re-encode of a video's
     soundtrack), not where a true-peak ceiling is promised on a master.

     Streaming: push(channelArrays[, frames]) → array of emitted blocks (each a
     freshly allocated channelArrays); flush() → { blocks, maxGrDb }. Output is
     delayed by exactly A samples inside the object and comes out equal in
     length to the input; blocks concatenated are bit-identical to a single
     push of the whole stream (tests/js/mastering-limiter-fast.test.mjs). */
  function createFastLimiter(sr, nCh, opts) {
    opts = opts || {};
    var L = dbToLin((typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0);
    /* `attackMs` is this limiter's gain WINDOW: a minimum held over it, then a
       moving average of the same length. Same construction as the mastering
       gain computer above and the same reason for its length — under 12 ms the
       gain can move inside a cycle of a bass note and becomes distortion on it.
       Measured on a sustained 41 Hz note under a transient train, floor 0.10 %:
       at 5 ms 0.88 / 1.14 / 1.31 % for 7.6 / 10.6 / 14.6 dB of limiting, at
       20 ms 0.14 / 0.19 / 0.19 %. `windowMs` is the name that says what it is;
       `attackMs` stays for the callers that already pass it. */
    var A = Math.max(1, Math.round(((opts.windowMs != null) ? opts.windowMs
                                  : (opts.attackMs != null) ? opts.attackMs : 20) / 1000 * sr));
    var relMs = (opts.releaseMs != null) ? opts.releaseMs : 50;
    var relK = 1 - Math.exp(-1 / Math.max(1, relMs / 1000 * sr));   // per-sample release step
    var cap = Math.max(4 * A, 1 << 16);
    var buf = [], c;
    for (c = 0; c < nCh; c++) buf.push(new Float32Array(cap));
    var gt = new Float32Array(cap);                 // per-sample target gain of the pending samples
    var len = 0;                                    // pending samples (real + virtual tail)
    var real = 0;                                   // how many of them are real (flush pads virtual gt=1)
    var proc = 0;                                   // next sample to output (index into buf)
    var fed = 0;                                    // gt computed up to here
    // monotonic deque over gt for the window [n, n+A): indices into buf
    var dqCap = 1; while (dqCap < A + 2) dqCap <<= 1;
    var dqIdx = new Int32Array(dqCap), dqHead = 0, dqTail = 0, dqNext = 0;   // dqNext: next index to admit
    var ring = new Float32Array(A), ringPos = 0, maSum = A;                  // MA over the last A minima, pre-roll = 1
    for (var r0 = 0; r0 < A; r0++) ring[r0] = 1;
    var env = 1, minEnv = 1;

    function ensure(need) {
      if (need <= buf[0].length) return;
      var n2 = buf[0].length; while (n2 < need) n2 *= 2;
      for (var k = 0; k < nCh; k++) { var g = new Float32Array(n2); g.set(buf[k].subarray(0, len)); buf[k] = g; }
      var g2 = new Float32Array(n2); g2.set(gt.subarray(0, len)); gt = g2;
    }
    function compact() {
      // keep [proc-1, len): after drain() the deque's front is ≥ proc-1
      var shift = proc - 1;
      if (shift < A || shift < (len >> 1)) return;
      for (var k = 0; k < nCh; k++) buf[k].copyWithin(0, shift, len);
      gt.copyWithin(0, shift, len);
      for (var q = dqHead; q !== dqTail; q = (q + 1) & (dqCap - 1)) dqIdx[q] -= shift;
      len -= shift; real -= shift; fed -= shift; dqNext -= shift; proc -= shift;
    }
    function computeGt(from, to) {
      for (var n = from; n < to; n++) {
        var p = 0;
        for (var k = 0; k < nCh; k++) { var v = buf[k][n]; if (v < 0) v = -v; if (v > p) p = v; }
        gt[n] = p > L ? L / p : 1;
      }
    }
    // Emit everything whose lookahead window is complete: n + A ≤ len.
    function drain() {
      var end = Math.min(len - A, real);
      if (end <= proc) return null;
      var out = [], k, n;
      for (k = 0; k < nCh; k++) out.push(new Float32Array(end - proc));
      var mask = dqCap - 1;
      for (n = proc; n < end; n++) {
        // admit gt[dqNext .. n+A-1] into the deque (monotonic increasing values)
        var lim = n + A;
        while (dqNext < lim) {
          var v = gt[dqNext];
          while (dqHead !== dqTail) {
            var last = (dqTail - 1) & mask;
            if (gt[dqIdx[last]] >= v) dqTail = last; else break;
          }
          dqIdx[dqTail] = dqNext; dqTail = (dqTail + 1) & mask; dqNext++;
        }
        while (dqIdx[dqHead] < n) dqHead = (dqHead + 1) & mask;   // expire indices behind the window
        var m = gt[dqIdx[dqHead]];
        maSum += m - ring[ringPos]; ring[ringPos] = m; ringPos++; if (ringPos === A) ringPos = 0;
        var ma = maSum / A; if (ma > 1) ma = 1;                    // float drift guard
        env = (ma < env) ? ma : env + (ma - env) * relK;
        if (env < minEnv) minEnv = env;
        for (k = 0; k < nCh; k++) out[k][n - proc] = buf[k][n] * env;
      }
      proc = end;
      return out;
    }
    return {
      push: function (channelArrays, frames) {
        var n = frames || (channelArrays[0] ? channelArrays[0].length : 0);
        if (!n) return [];
        if (real !== len) throw new Error('createFastLimiter: push after flush');
        ensure(len + n);
        for (var k = 0; k < nCh; k++) buf[k].set(channelArrays[k].subarray(0, n), len);
        len += n; real = len;
        computeGt(fed, len); fed = len;
        var b = drain();
        compact();
        return b ? [b] : [];
      },
      flush: function () {
        // A virtual samples of gt = 1 complete the last windows; nothing real is appended.
        ensure(len + A);
        for (var k = 0; k < nCh; k++) buf[k].fill(0, len, len + A);
        gt.fill(1, len, len + A);
        len += A; fed = len;
        var b = drain();
        return { blocks: b ? [b] : [], maxGrDb: minEnv > 0 ? Math.max(0, -20 * Math.log10(minEnv)) : Infinity };
      }
    };
  }

  /* Whole-buffer convenience over createFastLimiter: { out, maxGrDb }. */
  function limitFast(channels, sr, opts) {
    var lim = createFastLimiter(sr, channels.length, opts);
    var blocks = lim.push(channels);
    var tail = lim.flush();
    blocks = blocks.concat(tail.blocks);
    var N = channels[0].length, out = [], c, b, off;
    for (c = 0; c < channels.length; c++) {
      var o = new Float32Array(N); off = 0;
      for (b = 0; b < blocks.length; b++) { o.set(blocks[b][c], off); off += blocks[b][c].length; }
      out.push(o);
    }
    return { out: out, maxGrDb: tail.maxGrDb };
  }

  var API = { buildEnvelope: buildEnvelope, buildEnvelopeAsync: buildEnvelopeAsync, limit: limit, limitAsync: limitAsync, slidingMinForward: slidingMinForward, createStreamLimiter: createStreamLimiter, createFastLimiter: createFastLimiter, limitFast: limitFast };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (typeof window !== 'undefined') window.MasteringLimiter = API;
  // A worker has no window: the stretch worker importScripts this file to keep
  // its own output under full scale (export-ceiling.js limitChannels).
  else if (root) root.MasteringLimiter = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
